import { CurrencyView } from '../currency-view';
import { BlockLoading } from '../fetch-status/block-loading';
import { Button } from '../ui/button';
import { ClaimButton } from './claim-button';
import { useAppSettingStore } from '@/client/store/app-store';
import { useModelStore } from '@/client/store/model-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { useUserPositions } from '@/libs/react-sdk/api/use-user-options';
import { ClaimStatus, MarketStatus } from '@/libs/sdk/api';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';

type ViewDetailsProps = {
  marketAddress: string;
};
export const ViewDetails = ({ marketAddress }: ViewDetailsProps) => {
  const { t } = useTranslation();
  const [location, navigate] = useLocation();
  const isMarketPage = useMemo(() => location.startsWith('/market'), [location]);
  const setTradeOpen = useModelStore(state => state.setTradeOpen);
  if (isMarketPage) return null;
  return (
    <Button
      variant="default"
      size="lg"
      onClick={() => {
        navigate(`/market/${marketAddress}`);
        setTradeOpen(false);
      }}
      className="mt-4 bg-foreground/10 p-4 text-center hover:text-white text-sm  rounded-3 font-semibold leading-none w-full"
    >
      {t('view-details')}
    </Button>
  );
};

type UserEaringsProps = {
  className?: string;
  marketAddress: string;
  // This settled market's own collateral (from the loaded market detail). Scopes
  // the claimable-positions query to the market's currency, not the global lens.
  baseTokenAddress?: string;
  optionAddress: string;
  marketStatus: MarketStatus;
  isClaim: ClaimStatus;
  optionsLen: number;
  uid: string;
};
export const UserEarings = ({
  className,
  marketAddress,
  baseTokenAddress,
  marketStatus,
  optionAddress,
  optionsLen,
  isClaim,
  uid,
}: UserEaringsProps) => {
  const { t } = useTranslation();
  const storeBaseTokenAddress = useAppSettingStore(state => state.baseToken.address);
  const params = useMemo(() => {
    return {
      baseTokenAddress: baseTokenAddress ?? storeBaseTokenAddress,
      page: 1,
      pageSize: optionsLen,
      marketAddress: marketAddress,
      uid,
    };
  }, [baseTokenAddress, storeBaseTokenAddress, marketAddress, optionsLen, uid]);
  const userPositions = useUserPositions(api, params, {
    enabled: marketStatus === MarketStatus.closed,
  });
  const positions = useMemo(() => {
    const raw = userPositions.data?.pages.flatMap(item => item.positions) || [];
    if (marketStatus !== MarketStatus.closed || !optionAddress) return raw;
    // Override prices for resolved markets: winning option = 1, losing = 0
    return raw.map(item => {
      const decimal = item.optionDecimal ?? 6;
      const one = String(10 ** decimal);
      const isWinner = item.optionAddress === optionAddress;
      const price = isWinner ? one : '0';
      const total = isWinner ? item.toWin : '0';
      return { ...item, marketPirce: price, currentValue: total };
    });
  }, [userPositions.data?.pages, marketStatus, optionAddress]);
  // Check if all positions are already claimed (user-level, from positions API)
  const allClaimed = useMemo(
    () => positions.length > 0 && positions.every(p => p.isClaimed === ClaimStatus.yes),
    [positions]
  );
  if (marketStatus < MarketStatus.settlement) return null;
  if (userPositions.isLoading) return <BlockLoading />;
  if (positions.length === 0 || isClaim == ClaimStatus.yes || allClaimed || marketStatus <= MarketStatus.disputed) {
    return <ViewDetails marketAddress={marketAddress} />;
  }
  return (
    <div className={cn('space-y-2', className)}>
      <div className="space-y-2 max-h-95 overflow-y-auto">
        {positions.map(item => (
          <div
            key={`user-earings-${item.optionAddress}`}
            className="bg-foreground/4 p-4 rounded-3 text-sm  leading-[normal] space-y-3"
          >
            <h3 className="font-semibold text-foreground">{t('userearings')}</h3>
            <div className="flex items-center justify-between">
              <span className="text-foreground/70">{t('option')}</span>
              <span className="font-semibold">{item.optionDescription ?? item.optionName}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-foreground/70">{t('position')}</span>
              <span className="font-semibold">
                <CurrencyView value={item.toWin} decimal={item.optionDecimal} />
                {t('shares')}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-foreground/70">{t('value-per-share')}</span>
              <CurrencyView
                className="font-semibold inline-flex items-center"
                showIcon={true}
                baseTokenAddress={baseTokenAddress}
                value={item.marketPirce}
                decimal={item.optionDecimal}
                precision={2}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-foreground/70">{t('total')}</span>
              <CurrencyView
                className="font-semibold inline-flex items-center"
                showIcon={true}
                baseTokenAddress={baseTokenAddress}
                value={item.currentValue}
                decimal={item.optionDecimal}
                precision={2}
              />
            </div>
          </div>
        ))}
      </div>
      <ClaimButton
        disabled={marketStatus !== MarketStatus.closed}
        marketAddress={marketAddress}
        optionAddress={optionAddress}
      />
    </div>
  );
};
